import { useEffect, useRef, useState } from 'react'
import { SOUND_TEST_URL } from '@/features/twilio/TwilioConstants'
import { useMicrophoneLevels } from '@/features/twilio/hooks/useMicrophoneLevels'
import { useSpeakerLevels } from '@/features/twilio/hooks/useSpeakerLevels'
import { useRecorder } from '@/features/twilio/hooks/useRecorder'
import useInterval from '@/shared/hooks/useInterval'
import { TwilioContext } from '@/lib/store'
import { RecordingStatus } from '@/features/twilio/components/DeviceSettings'

export function useDeviceSettings(
  twilioContext: TwilioContext,
  changeOutputDevice: (deviceId: string) => void,
  changeInputDevice: (deviceId: string) => void,
) {
  const [speakerIsInUse, setSpeakerIsInUse] = useState<boolean>(false)
  const setSpeakerIsInUseCallback = useRef<
    ((playing: boolean) => void) | null
  >(null)
  useEffect(() => {
    setSpeakerIsInUseCallback.current = (playing) => setSpeakerIsInUse(playing)
  }, [setSpeakerIsInUse])

  // For some reason this audio element cannot be created in a hook
  const audioElement = useRef<HTMLAudioElement | null>(null)
  if (!audioElement.current) {
    audioElement.current = new Audio(SOUND_TEST_URL)
    audioElement.current.setAttribute('crossorigin', 'anonymous')
    audioElement.current.loop = false
    audioElement.current.onended = () => {
      setSpeakerIsInUseCallback.current?.(false)
    }
  }

  const { availableInput, selectedInput, availableOutput, selectedOutput } =
    twilioContext

  const microphone = useMicrophoneLevels(
    twilioContext.audioContext,
    selectedInput.deviceId,
  )

  const speaker = useSpeakerLevels(
    twilioContext.audioContext,
    audioElement.current,
    selectedOutput?.deviceId ?? null,
  )

  const recorder = useRecorder(selectedInput.deviceId)
  const [recorderElapsedDuration, setRecorderElapsedDuration] = useState(0)
  useInterval(
    () => {
      setRecorderElapsedDuration(Date.now() - recorder.recordingStartTime)
    },
    recorder.isRecording ? 500 : undefined,
  )

  function playRecordedAudio() {
    if (recorder.recordedMediaDuration && audioElement.current) {
      audioElement.current.src = recorder.getUrl()
      audioElement.current.play()
      setSpeakerIsInUse(true)
    }
  }

  function startRecording() {
    setRecorderElapsedDuration(0)
    recorder.start()
  }

  function stopRecording() {
    recorder.stop()
  }

  return {
    speakerIsInUse,
    availableInputDevices: availableInput,
    selectedInput,
    availableOutputDevices: availableOutput,
    selectedOutput,
    microphone,
    inputLevel: microphone.levels,
    speaker,
    outputLevel: speaker.levels,
    recorder,
    recorderElapsedDuration,
    changeInputDevice,
    changeOutputDevice,
    recordingStatus: (recorder.isRecording
      ? {
          status: 'RECORDING',
          elapsedMs: recorderElapsedDuration,
        }
      : { status: 'IDLE' }) as RecordingStatus,
    recording: recorder.recordedMediaDuration
      ? { durationInMs: recorder.recordedMediaDuration }
      : null,
    selectedOutputDeviceId: selectedOutput?.deviceId ?? null,
    selectedInputDeviceId: selectedInput.deviceId,
  }
}
